import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '@/store/auth';
import { FileText, Upload, ExternalLink, Download } from 'lucide-react';

interface NormaItem {
  codigo: string;
  tipo: string;
  numero: string;
  organismo: string;
  fecha_emision: string | null;
  materia: string;
  url_oficial: string | null;
  archivo_url: string | null;
  archivo_nombre: string | null;
  archivo_tamano_bytes: number | null;
  fecha_carga: string | null;
  notas: string | null;
}

const TIPO_PILL: Record<string, string> = {
  ley: 'bg-purple-100 text-purple-700',
  resolucion: 'bg-blue-100 text-blue-700',
  reglamento: 'bg-amber-100 text-amber-700',
};

const ORG_PILL: Record<string, string> = {
  JNE: 'bg-blue-100 text-blue-800',
  ONPE: 'bg-green-100 text-green-800',
  Congreso: 'bg-purple-100 text-purple-800',
  OP: 'bg-amber-100 text-amber-800',
};

export function Normativa() {
  const [sp] = useSearchParams();
  const { rol } = useAuth();
  const qc = useQueryClient();
  const [tipo, setTipo] = useState(sp.get('tipo') ?? '');
  const [organismo, setOrganismo] = useState(sp.get('organismo') ?? '');
  const [q, setQ] = useState(sp.get('q') ?? '');
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const canUpload = ['PLT', 'PLA', 'ADM'].includes(rol ?? '');

  const { data, isLoading } = useQuery<{ total: number; items: NormaItem[] }>({
    queryKey: ['normativa', tipo, organismo, q],
    queryFn: async () => (await api.get('/normativa', {
      params: { tipo: tipo || undefined, organismo: organismo || undefined, q: q || undefined },
    })).data,
  });

  const uploadMut = useMutation({
    mutationFn: async ({ codigo, file }: { codigo: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/normativa/${encodeURIComponent(codigo)}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['normativa'] }),
  });

  const downloadNorma = async (codigo: string, archivoNombre: string | null) => {
    const r = await api.get(`/normativa/${encodeURIComponent(codigo)}/file`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = archivoNombre ?? `${codigo}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-4 max-w-[1300px]">
      <header>
        <h1 className="text-xl font-bold text-arpp-primary">Normativa Electoral ERM2026</h1>
        <p className="text-xs text-gray-600">
          {data?.total ?? '…'} normas catalogadas · Resoluciones JNE, ONPE, Leyes, Reglamentos
        </p>
      </header>

      <div className="flex flex-wrap gap-3 items-end bg-white border rounded p-3">
        <div>
          <label className="block text-[11px] text-gray-600">Búsqueda</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} className="border rounded px-2 py-1 text-sm w-64" placeholder="código / número / materia" />
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="border rounded px-2 py-1 text-sm">
            <option value="">Todos</option>
            <option value="ley">Ley</option>
            <option value="resolucion">Resolución</option>
            <option value="reglamento">Reglamento</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Organismo</label>
          <select value={organismo} onChange={(e) => setOrganismo(e.target.value)} className="border rounded px-2 py-1 text-sm">
            <option value="">Todos</option>
            <option value="JNE">JNE</option>
            <option value="ONPE">ONPE</option>
            <option value="Congreso">Congreso</option>
            <option value="OP">OP / Partido</option>
          </select>
        </div>
      </div>

      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs">
            <tr>
              <th className="px-3 py-2 text-left">Código</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Organismo</th>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Materia</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="p-4 text-gray-500">Cargando…</td></tr>}
            {data?.items.map((n) => (
              <tr key={n.codigo} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-xs">
                  <a id={n.codigo} />
                  <strong className="text-arpp-primary">{n.codigo}</strong>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${TIPO_PILL[n.tipo] ?? 'bg-gray-100'}`}>{n.tipo}</span>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${ORG_PILL[n.organismo] ?? 'bg-gray-100'}`}>{n.organismo}</span>
                </td>
                <td className="px-3 py-2 text-xs">{n.fecha_emision ?? '—'}</td>
                <td className="px-3 py-2 text-xs">{n.materia}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {n.url_oficial && (
                      <a href={n.url_oficial} target="_blank" rel="noopener"
                         className="text-arpp-primary hover:bg-gray-100 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"
                         title="URL oficial">
                        <ExternalLink size={12} />
                      </a>
                    )}
                    {n.archivo_url ? (
                      <button onClick={() => downloadNorma(n.codigo, n.archivo_nombre)}
                              className="text-green-700 hover:bg-green-50 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"
                              title={`Descargar PDF (${n.archivo_nombre})`}>
                        <Download size={12} /> PDF
                      </button>
                    ) : (
                      <span className="text-gray-400 text-[11px] inline-flex items-center gap-1">
                        <FileText size={11} /> sin PDF
                      </span>
                    )}
                    {canUpload && (
                      <>
                        <input type="file" accept=".pdf"
                               ref={(el) => (fileRefs.current[n.codigo] = el)}
                               className="hidden"
                               onChange={(e) => {
                                 const f = e.target.files?.[0];
                                 if (f) uploadMut.mutate({ codigo: n.codigo, file: f });
                                 e.target.value = '';
                               }} />
                        <button onClick={() => fileRefs.current[n.codigo]?.click()}
                                className="text-gray-600 hover:bg-gray-100 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"
                                title={n.archivo_url ? 'Reemplazar PDF' : 'Subir PDF'}>
                          <Upload size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
